import { INVOICE_DIRECTION_LABELS, invoiceDirectionSchema, type InvoiceDirection } from "@station/contracts";
import { toIstanbulDate } from "@station/core";
import {
  Alert,
  Button,
  EmptyState,
  SkeletonRows,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ShieldAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { companySearchShape } from "../../../features/accounting/company.js";
import { InvoiceForm } from "../../../features/invoicing/invoice-form.js";
import {
  blankInvoiceHeader,
  blankInvoiceLine,
  blankReceiptRow,
  returnLinesFrom,
} from "../../../features/invoicing/invoice-values.js";
import { useEarchiveRequest, useInvoice } from "../../../features/invoicing/queries.js";
import {
  InvoicingPage,
  useInvoicingCrumb,
  useInvoicingPermissions,
} from "../../../features/invoicing/shared.js";
import { errorMessage } from "../../../lib/errors.js";

const searchSchema = z.object({
  ...companySearchShape,
  direction: invoiceDirectionSchema.optional().catch(undefined),
  /** İade faturası: asıl faturanın kimliği. */
  iade: z.string().uuid().optional().catch(undefined),
  /** POS e-Arşiv talebinden gelindi. */
  talep: z.string().uuid().optional().catch(undefined),
});

export const Route = createFileRoute("/_app/faturalar/yeni")({
  validateSearch: (search) => searchSchema.parse(search),
  component: NewInvoicePage,
});

const DIRECTIONS: InvoiceDirection[] = ["SALES", "PURCHASE"];

function NewInvoicePage() {
  const { t } = useTranslation();
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  const crumb = useInvoicingCrumb("/faturalar");
  const permissions = useInvoicingPermissions();
  const direction = search.direction ?? "SALES";
  const returnOf = useInvoice(search.iade ?? null);
  const request = useEarchiveRequest(search.talep ?? null);
  const locked = Boolean(search.iade);

  return (
    <InvoicingPage
      title={t("menu.invoiceNew")}
      description={t("invoicing.form.description")}
      breadcrumb={[crumb, { label: t("menu.invoiceNew") }]}
    >
      {({ companyId }) => {
        if (search.iade && returnOf.isPending) return <SkeletonRows rows={8} />;
        if (search.iade && returnOf.error) return <Alert tone="danger">{errorMessage(returnOf.error)}</Alert>;
        if (search.talep && request.isPending) return <SkeletonRows rows={8} />;
        if (search.talep && request.error) return <Alert tone="danger">{errorMessage(request.error)}</Alert>;

        const original = search.iade ? returnOf.data : undefined;
        const posRequest = search.talep ? request.data : undefined;
        const effectiveDirection = original ? original.direction : direction;
        const canWrite =
          effectiveDirection === "SALES" ? permissions.canManageSales : permissions.canManagePurchase;

        if (posRequest && !posRequest.matchedCounterpartyId) {
          return (
            <EmptyState
              headingLevel={2}
              icon={<ShieldAlert />}
              title={t("invoicing.requests.needsCounterpartyTitle")}
              description={t("invoicing.requests.needsCounterpartyBody", {
                name: posRequest.buyerName,
                taxId: posRequest.buyerTaxId,
              })}
              action={
                <Button asChild>
                  <Link to="/cari/yeni">{t("invoicing.requests.createCounterparty")}</Link>
                </Button>
              }
            />
          );
        }

        if (!canWrite) {
          return (
            <EmptyState
              headingLevel={2}
              icon={<ShieldAlert />}
              title={t("invoicing.form.noPermission")}
              description={t("invoicing.form.noPermissionBody")}
            />
          );
        }

        const today = toIstanbulDate(new Date());
        const header = blankInvoiceHeader(effectiveDirection, today, {
          ...(original
            ? {
                invoiceType: "IADE" as const,
                counterpartyId: original.counterpartyId,
                stationId: original.stationId,
                originalInvoiceId: original.id,
                pricingMode: original.pricingMode,
                documentDiscountKind: original.documentDiscount?.kind ?? "NONE",
                documentDiscountValue: original.documentDiscount?.value ?? null,
              }
            : {}),
          ...(posRequest
            ? {
                counterpartyId: posRequest.matchedCounterpartyId,
                stationId: posRequest.stationId,
                earchiveRequestId: posRequest.id,
                basis: "FISCAL_RECEIPTS" as const,
              }
            : {}),
        });
        const lines = original ? returnLinesFrom(original) : [blankInvoiceLine()];
        const receipts =
          posRequest && posRequest.fiscalDeviceSerial
            ? [
                {
                  ...blankReceiptRow(posRequest.stationId),
                  fiscalDeviceSerial: posRequest.fiscalDeviceSerial,
                  zNo: String(posRequest.zNo ?? ""),
                  receiptNo: String(posRequest.receiptNo ?? ""),
                  receiptDate: posRequest.receiptDate,
                  amount: posRequest.amount,
                },
              ]
            : [];

        const form = (
          <InvoiceForm
            // Yön / asıl fatura / talep değişince form baştan kurulur (yarım kalmış durum taşınmaz).
            key={`${effectiveDirection}-${search.iade ?? ""}-${search.talep ?? ""}`}
            companyId={companyId}
            initialHeader={header}
            initialLines={lines}
            initialReceipts={receipts}
            saved={null}
            lockKind={locked}
            {...(original
              ? { notice: t("invoicing.form.returnNotice", { no: original.invoiceNo ?? "" }) }
              : posRequest
                ? { notice: t("invoicing.form.requestNotice", { name: posRequest.buyerName }) }
                : {})}
          />
        );
        // İade ve POS talebinde yön kilitlidir; yalnızca serbest girişte yön sekmesi gösterilir.
        if (locked || posRequest) return form;
        return (
          <Tabs
            value={direction}
            onValueChange={(next) =>
              void navigate({
                search: (previous) => ({ ...previous, direction: next as InvoiceDirection }),
                replace: true,
              })
            }
          >
            <TabsList aria-label={t("invoicing.form.directionTabs")}>
              {DIRECTIONS.map((option) => (
                <TabsTrigger key={option} value={option}>
                  {INVOICE_DIRECTION_LABELS[option]}
                </TabsTrigger>
              ))}
            </TabsList>
            {DIRECTIONS.map((option) => (
              <TabsContent key={option} value={option}>
                {direction === option ? form : null}
              </TabsContent>
            ))}
          </Tabs>
        );
      }}
    </InvoicingPage>
  );
}
